import { useEffect, useMemo, useRef, useState } from "react";
import { CalendarDays, ChevronDown, SlidersHorizontal, X } from "lucide-react";
import { useMyAssistant } from "@/hooks/useMyAssistant";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/i18n/LanguageContext";
import { CALENDAR_CHANGED_EVENT, useRetailCalendar } from "@/hooks/useRetailCalendar";
import { CAL_COLORS, cleanColorLabels, type CalColor, type CalColorLabels, type CalendarItem } from "@/lib/retailCalendarData";
import { CAL_DOT, CalendarColorWheel } from "@/components/roadmap/CalendarColorWheel";
import { refreshCalendarSuggestions } from "@/lib/retailCalendar.functions";

type T = (s: string, k: string) => string;

export const itemTitle = (i: CalendarItem, t: T) => i.title ?? t("retailCalEvents", `${i.systemKey?.split("@")[0]}_t`);
const itemReason = (i: CalendarItem, t: T) => i.reason ?? (i.systemKey ? t("retailCalEvents", `${i.systemKey.split("@")[0]}_r`) : "");

export function whenLabel(n: number, t: T) {
  if (n < 0) return t("retailCal", "ongoing");
  if (n === 0) return t("retailCal", "today");
  if (n === 1) return t("retailCal", "inOneDay");
  return t("retailCal", "inDays").replace("{n}", String(n));
}

const DOT = CAL_DOT;
const dotClass = (i: CalendarItem) => (i.color ? DOT[i.color] : i.source === "user_custom" ? DOT.graphite : "bg-primary ring-1 ring-foreground/25");
const barClass = (i: CalendarItem) => (i.color ? DOT[i.color] : i.source === "user_custom" ? DOT.graphite : "bg-primary");
const isSpan = (i: CalendarItem) => !!i.end && i.end > i.start;
const LEADS = [7, 14, 21, 30, 45];
const emptyForm = { id: "", start: "", end: "", title: "", reason: "", lead: 21, color: "graphite" as CalColor };
const notify = () => window.dispatchEvent(new Event(CALENDAR_CHANGED_EVENT));
const isoDay = (y: number, m: number, d: number) => new Date(Date.UTC(y, m, d)).toISOString().slice(0, 10);
const covers = (i: CalendarItem, day: string) => i.start <= day && (i.end ?? i.start) >= day;

interface Props {
  userId: string;
  /** Avisa al panel para colocar el calendario a la izquierda mientras Sara IA ayuda. */
  onSaraChange?: (open: boolean) => void;
  onSwap?: () => void;
  /** Vista desplegable compacta: una sola columna con pestañas y deslizamiento. */
  compact?: boolean;
}

/** Calendario comercial integrado en la Hoja de Ruta: mes, fechas y Sara IA en el mismo plano. */
export default function RetailCalendarSummaryCard({ userId, onSaraChange, onSwap, compact = false }: Props) {
  const { t, lang } = useLanguage();
  const locale = lang === "es" ? "es-ES" : "en-GB";
  const now = new Date();
  const todayIso = now.toISOString().slice(0, 10);
  const [open, setOpen] = useState(true);
  const [view, setView] = useState({ y: now.getUTCFullYear(), m: now.getUTCMonth() });
  const [selected, setSelected] = useState<string | null>(null);
  const [form, setForm] = useState<typeof emptyForm | null>(null);
  const [noteFor, setNoteFor] = useState<string | null>(null);
  const [infoFor, setInfoFor] = useState<string | null>(null);
  const infoTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoverInfo = (id: string | null) => {
    if (typeof window === "undefined" || !window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
    if (infoTimer.current) clearTimeout(infoTimer.current);
    infoTimer.current = setTimeout(() => setInfoFor(id), id ? 80 : 180);
  };
  const [sara, setSara] = useState<"off" | "loading" | "ready" | "error">("off");
  const askedMonths = useRef(new Set<string>());
  const [labels, setLabels] = useState<CalColorLabels>({});
  const [legendEdit, setLegendEdit] = useState<{ orig: CalColor | null; color: CalColor; name: string } | null>(null);
  const [formWheel, setFormWheel] = useState(false);
  const [pane, setPane] = useState<"month" | "dates">("month");
  const [rightTab, setRightTab] = useState<"dates" | "sara">("dates");
  // Siempre el nombre del avatar que eligió el empresario (Sara por defecto).
  const myAssistant = useMyAssistant();
  const aiName = `${myAssistant?.displayName || "Sara"} ${lang === "es" ? "IA" : "AI"}`;
  const tt = (k: string) => t("retailCal", k).split("{ai}").join(aiName);
  const touch = useRef<{ x: number; y: number } | null>(null);

  const [brandName, setBrandName] = useState("");
  const [brandEdit, setBrandEdit] = useState<string | null>(null);
  const [ask, setAsk] = useState("");

  useEffect(() => {
    if (!userId) return;
    void supabase.from("user_preferences").select("calendar_color_labels" as any).eq("user_id", userId).maybeSingle()
      .then(({ data }) => {
        const raw = (data as any)?.calendar_color_labels;
        setLabels(cleanColorLabels(raw));
        setBrandName(typeof raw?.brand === "string" ? raw.brand.trim().slice(0, 40) : "");
      });
  }, [userId]);
  // El nombre del amarillo de marca viaja en la misma preferencia, bajo la clave «brand».
  const saveLabels = async (next: CalColorLabels, brand = brandName) => {
    setLabels(next);
    const payload = brand ? { ...next, brand } : next;
    const { error } = await supabase.from("user_preferences").upsert({ user_id: userId, calendar_color_labels: payload, updated_at: new Date().toISOString() } as any, { onConflict: "user_id" });
    if (error) toast.error(t("retailCal", "saveError"));
  };
  const closeBrandEdit = () => {
    if (brandEdit === null) return;
    const n = brandEdit.trim().slice(0, 40);
    setBrandEdit(null);
    if (n === brandName) return;
    setBrandName(n);
    void saveLabels(labels, n);
  };
  const closeLegendEdit = () => {
    if (!legendEdit) return;
    const next = { ...labels };
    if (legendEdit.orig && legendEdit.orig !== legendEdit.color) delete next[legendEdit.orig];
    const n = legendEdit.name.trim().slice(0, 40);
    if (n) next[legendEdit.color] = n; else delete next[legendEdit.color];
    setLegendEdit(null);
    if (JSON.stringify(next) !== JSON.stringify(labels)) void saveLabels(next);
  };
  const removeLegend = () => {
    if (!legendEdit) return;
    const next = { ...labels };
    if (legendEdit.orig) delete next[legendEdit.orig];
    setLegendEdit(null);
    void saveLabels(next);
  };
  const labelled = CAL_COLORS.filter((c) => labels[c]);
  const colorName = (c: CalColor) => labels[c] ?? t("retailCal", `color_${c}`);

  const monthFrom = isoDay(view.y, view.m, 1);
  const daysInMonth = new Date(Date.UTC(view.y, view.m + 1, 0)).getUTCDate();
  const monthKey = monthFrom.slice(0, 7);
  const cal = useRetailCalendar(userId, daysInMonth - 1, monthFrom);

  useEffect(() => { onSaraChange?.(sara !== "off"); }, [sara, onSaraChange]);

  const fmtMonth = new Intl.DateTimeFormat(locale, { month: "long", year: "numeric", timeZone: "UTC" });
  const fmtShort = new Intl.DateTimeFormat(locale, { month: "short", timeZone: "UTC" });
  const fmtWeekday = new Intl.DateTimeFormat(locale, { weekday: "narrow", timeZone: "UTC" });
  const weekdays = Array.from({ length: 7 }, (_, i) => fmtWeekday.format(new Date(Date.UTC(2024, 0, 1 + i))));
  const rawMonth = fmtMonth.format(new Date(`${monthFrom}T00:00:00Z`));
  const monthLabel = rawMonth.charAt(0).toUpperCase() + rawMonth.slice(1);

  const marked = cal.items.filter((i) => i.status === "active");
  const suggested = cal.items.filter((i) => i.status === "suggested");
  const shown = cal.items.filter((i) => i.status !== "suggested" && (selected ? covers(i, selected) : true));
  // Campañas de varios días: cada una en su carril para que las líneas no se pisen (máx. 3).
  const laneOf = useMemo(() => {
    const lanes: CalendarItem[][] = [];
    for (const e of [...marked].filter(isSpan).sort((a, b) => a.start.localeCompare(b.start))) {
      const free = lanes.find((l) => (l[l.length - 1]!.end ?? l[l.length - 1]!.start) < e.start);
      if (free) free.push(e); else if (lanes.length < 3) lanes.push([e]);
    }
    return { lanes };
  }, [marked]);
  const openBigChat = () => {
    window.dispatchEvent(new CustomEvent("open-support-chat", { detail: { prefill: t("retailCal", "saraBigPrefill").replace("{month}", monthLabel) } }));
  };

  const cells = useMemo(() => {
    const lead = (new Date(Date.UTC(view.y, view.m, 1)).getUTCDay() + 6) % 7;
    return [...Array(lead).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => isoDay(view.y, view.m, i + 1))] as (string | null)[];
  }, [view, daysInMonth]);

  const move = (delta: number) => {
    setSelected(null);
    setView((v) => { const d = new Date(Date.UTC(v.y, v.m + delta, 1)); return { y: d.getUTCFullYear(), m: d.getUTCMonth() }; });
  };

  const askSara = (month = monthKey, request?: string) => {
    setRightTab("sara");
    if (!request && askedMonths.current.has(month)) { setSara("ready"); return; }
    askedMonths.current.add(month);
    setSara("loading");
    refreshCalendarSuggestions({ data: { lang, month, ...(request ? { request } : {}) } })
      .then((r) => {
        setSara(r.ok ? "ready" : "error");
        if (r.ok && r.added) notify();
        // Si Sara encuentra la fecha en otro mes, el calendario salta a ese mes.
        const fd = r.ok && "firstDate" in r ? r.firstDate : null;
        if (fd && fd.slice(0, 7) !== monthKey) {
          askedMonths.current.add(fd.slice(0, 7));
          setSelected(null);
          setView({ y: Number(fd.slice(0, 4)), m: Number(fd.slice(5, 7)) - 1 });
        }
      })
      .catch(() => setSara("error"));
  };
  // Con Sara abierta, al cambiar de mes revisa también ese mes.
  useEffect(() => { if (sara === "ready" || sara === "error") askSara(monthKey); }, [monthKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (p: PromiseLike<{ error: unknown }>) => {
    const { error } = await p;
    if (error) { toast.error(t("retailCal", "saveError")); return false; }
    notify();
    return true;
  };
  const baseRow = (i: CalendarItem) => ({ user_id: userId, system_key: i.systemKey, date_start: i.start, date_end: i.end, title: itemTitle(i, t), category: i.category, source: "system", lead_days: i.lead });
  const setStatus = (i: CalendarItem, status: "active" | "inactive") => i.rowId
    ? run(supabase.from("user_calendar_events").update({ status, updated_at: new Date().toISOString() }).eq("id", i.rowId))
    : run(supabase.from("user_calendar_events").insert({ ...baseRow(i), status }));
  const saveNote = async (i: CalendarItem, notes: string) => {
    const v = notes.trim().slice(0, 400) || null;
    setNoteFor(null);
    if ((i.notes ?? null) === v) return;
    const ok = i.rowId
      ? await run(supabase.from("user_calendar_events").update({ notes: v, updated_at: new Date().toISOString() }).eq("id", i.rowId))
      : await run(supabase.from("user_calendar_events").insert({ ...baseRow(i), status: i.status, notes: v }));
    if (ok) toast.success(t("retailCal", "saved"));
  };
  const acceptAll = async () => {
    const ids = suggested.map((s) => s.rowId).filter(Boolean) as string[];
    if (ids.length) await run(supabase.from("user_calendar_events").update({ status: "active" }).in("id", ids));
  };
  const submitForm = async () => {
    if (!form || !form.start || !form.title.trim()) return;
    const payload = { date_start: form.start, date_end: form.end && form.end >= form.start ? form.end : null, title: form.title.trim().slice(0, 120), reason: form.reason.trim().slice(0, 600) || null, lead_days: form.lead, color: form.color, updated_at: new Date().toISOString() };
    const ok = form.id
      ? await run(supabase.from("user_calendar_events").update(suggested.some((s) => s.rowId === form.id) ? { ...payload, status: "active" } : payload).eq("id", form.id))
      : await run(supabase.from("user_calendar_events").insert({ ...payload, category: "sales", user_id: userId, source: "user_custom", status: "active" }));
    if (ok) { toast.success(t("retailCal", "saved")); setForm(null); }
  };
  const remove = (i: CalendarItem) => i.rowId && run(supabase.from("user_calendar_events").delete().eq("id", i.rowId));

  const place = [cal.city, cal.region].filter(Boolean).join(", ");
  const field = "mt-1 w-full min-h-11 rounded-xl border border-border/80 bg-background px-3 text-sm font-normal focus:outline-none focus:ring-2 focus:ring-foreground/20";
  const textBtn = "inline-flex min-h-11 items-center px-2 text-xs font-semibold text-muted-foreground underline-offset-4 hover:text-foreground hover:underline";
  const dateTag = (iso: string) => { const d = new Date(`${iso}T00:00:00Z`); return { day: d.getUTCDate(), mon: fmtShort.format(d).replace(".", "") }; };

  // Propuestas del asistente: columna derecha, misma fila compacta que las fechas del mes.
  const showSaraRight = sara !== "off" && rightTab === "sara";
  const iconBtn = "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground";
  const saraRight = (
    <div aria-live="polite">
      {sara === "loading" ? (
        <div className="py-6 text-center">
          <p className="text-[13px] text-foreground/80">{tt("saraWorking")}</p>
          <div className="mx-auto mt-3 flex w-fit gap-1.5" aria-hidden="true">
            {[0, 150, 300].map((d) => <span key={d} className="h-1.5 w-1.5 animate-bounce rounded-full bg-foreground/50" style={{ animationDelay: `${d}ms` }} />)}
          </div>
        </div>
      ) : suggested.length === 0 ? (
        <p className="py-4 text-[13px] text-muted-foreground">{sara === "error" ? tt("radarError") : tt("saraEmptyRight")}</p>
      ) : (
        <>
          <ul className="divide-y divide-border/60">
            {suggested.map((s) => {
              const tag = dateTag(s.start);
              const infoId = `s:${s.id}`;
              return (
                <li key={s.id} className="flex items-center gap-2 py-1.5">
                  <span className="flex w-9 shrink-0 flex-col items-center rounded-lg border border-border/70 bg-background py-1 leading-none">
                    <span className="font-display text-base font-bold tabular-nums">{tag.day}</span>
                    <span className="mt-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">{tag.mon}</span>
                  </span>
                  <span className="flex min-w-0 flex-1 items-center gap-1.5">
                    <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dotClass(s)}`} aria-hidden="true" />
                    <span className="truncate font-semibold leading-snug">{s.title}</span>
                    <Popover open={infoFor === infoId} onOpenChange={(v) => setInfoFor(v ? infoId : null)}>
                      <PopoverTrigger asChild>
                        <button type="button" onMouseEnter={() => hoverInfo(infoId)} onMouseLeave={() => hoverInfo(null)} aria-label={t("retailCal", "moreInfo")} className="-my-3 -mx-2 inline-flex h-11 w-11 shrink-0 items-center justify-center">
                          <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full border font-display text-[11px] font-bold italic leading-none ${infoFor === infoId ? "border-foreground bg-foreground text-background" : "border-foreground/40 text-foreground/70"}`}>i</span>
                        </button>
                      </PopoverTrigger>
                      <PopoverContent align="start" sideOffset={4} onOpenAutoFocus={(e) => e.preventDefault()} onMouseEnter={() => hoverInfo(infoId)} onMouseLeave={() => hoverInfo(null)} className="w-[280px] rounded-2xl border-border/70 p-4 shadow-lg">
                        <span className="block text-[11px] uppercase tracking-wider text-muted-foreground">{tt("src_ai")}</span>
                        {s.reason && <span className="mt-1 block text-xs leading-snug text-foreground/85">{s.reason}</span>}
                        {labelled.length > 0 && (
                          <span className="mt-2 block">
                            <span className="block text-[11px] font-semibold text-muted-foreground">{t("retailCal", "pickColor")}</span>
                            <span className="mt-1 flex flex-wrap gap-1">
                              {labelled.map((c) => (
                                <button key={c} type="button" aria-pressed={s.color === c} onClick={() => s.rowId && run(supabase.from("user_calendar_events").update({ color: c }).eq("id", s.rowId))} className={`inline-flex min-h-11 items-center gap-2 rounded-full border px-3 text-xs font-medium ${s.color === c ? "border-foreground" : "border-border/70 hover:bg-muted"}`}>
                                  <span className={`h-3 w-3 rounded-full ${DOT[c]}`} />{labels[c]}
                                </button>
                              ))}
                            </span>
                          </span>
                        )}
                      </PopoverContent>
                    </Popover>
                  </span>
                  {s.rowId && (
                    <button type="button" onClick={() => setForm({ id: s.rowId!, start: s.start, end: s.end ?? "", title: s.title ?? "", reason: s.reason ?? "", lead: s.lead, color: s.color ?? labelled[0] ?? "graphite" })} aria-label={t("retailCal", "customize")} title={t("retailCal", "customize")} className={iconBtn}>
                      <SlidersHorizontal className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
                    </button>
                  )}
                  <label className="flex min-h-11 shrink-0 cursor-pointer items-center gap-1.5">
                    <span className="hidden text-[11px] font-semibold text-muted-foreground lg:inline">{t("retailCal", "activateCal")}</span>
                    <Switch checked={false} onCheckedChange={(v) => v && setStatus(s, "active")} aria-label={`${t("retailCal", "activateCalAria")}: ${s.title ?? ""}`} />
                  </label>
                  <button type="button" onClick={() => setStatus(s, "inactive")} aria-label={`${t("retailCal", "dismiss")}: ${s.title ?? ""}`} title={t("retailCal", "dismiss")} className={iconBtn}>
                    <X className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
                  </button>
                </li>
              );
            })}
          </ul>
          {suggested.length > 1 && (
            <button type="button" onClick={acceptAll} className="mt-2 min-h-11 rounded-full border border-foreground px-4 text-xs font-semibold hover:bg-muted">{t("retailCal", "acceptAll")}</button>
          )}
        </>
      )}
    </div>
  );

  return (
    <section className={compact ? "flex flex-col bg-card" : "flex h-full flex-col overflow-hidden rounded-2xl border border-border/70 bg-card shadow-[0_1px_2px_hsl(var(--foreground)/0.04),0_12px_32px_-16px_hsl(var(--foreground)/0.14)] ring-1 ring-inset ring-background"}>
      {!compact && (
      <div className="flex min-h-11 items-center gap-3 px-5 py-3">
        <span className="min-w-0 flex-1">
          <span className="block font-display text-base font-bold leading-tight">{t("retailCal", "title")}</span>
          <span className="block font-body text-xs text-muted-foreground">{t("retailCal", "calSub")}</span>
        </span>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={t("retailCal", open ? "collapse" : "expand")}
          className="inline-flex min-h-11 items-center gap-2 rounded-full px-3 text-foreground hover:bg-muted"
        >
          <CalendarDays className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? "" : "-rotate-90"}`} strokeWidth={1.5} aria-hidden="true" />
        </button>
      </div>
      )}

      {open && (
        <div className={`flex flex-1 flex-col font-body text-sm ${compact ? "" : "border-t border-border/70"}`}>
          {/* Móvil: dos vistas en horizontal (mes / fechas) */}
          <div className={`gap-1 px-4 pt-3 sm:hidden ${form ? "hidden" : "flex"}`} role="tablist">
            {(sara !== "off" ? (["month", "dates", "sara"] as const) : (["month", "dates"] as const)).map((p) => {
              const on = p === "month" ? pane === "month" : pane === "dates" && (p === "sara" ? rightTab === "sara" : rightTab !== "sara" || sara === "off");
              return (
                <button key={p} type="button" role="tab" aria-selected={on} onClick={() => { if (p === "month") setPane("month"); else { setPane("dates"); setRightTab(p === "sara" ? "sara" : "dates"); } }}
                  className={`min-h-11 flex-1 truncate rounded-full px-1 text-xs font-semibold transition-colors ${on ? "bg-foreground text-background" : "text-muted-foreground hover:bg-muted"}`}>
                  {p === "month" ? t("retailCal", "tabMonth") : p === "dates" ? t("retailCal", "tabDates") : aiName}
                  {p === "dates" && shown.length ? ` · ${shown.length}` : p === "sara" && suggested.length ? ` · ${suggested.length}` : ""}
                </button>
              );
            })}
          </div>
          <div
            className={`flex-1 gap-0 ${form ? "hidden" : "grid"} ${compact ? "sm:grid-cols-2" : "md:grid-cols-2"}`}
            onTouchStart={(e) => { const p0 = e.touches[0]; if (p0) touch.current = { x: p0.clientX, y: p0.clientY }; }}
            onTouchEnd={(e) => {
              const s = touch.current; touch.current = null;
              if (!s) return;
              const p1 = e.changedTouches[0]; if (!p1) return;
              const dx = p1.clientX - s.x, dy = p1.clientY - s.y;
              if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) move(dx < 0 ? 1 : -1);
            }}
          >
            {/* Mes */}
            <div className={`border-border/70 px-4 py-3 ${compact ? "sm:block sm:border-r" : "sm:block sm:border-b md:border-b-0 md:border-r"} ${pane === "month" ? "block" : "hidden"}`}>
              <div className="flex items-center justify-between">
                <button type="button" onClick={() => move(-1)} aria-label={t("retailCal", "prevMonth")} className="inline-flex h-11 w-11 items-center justify-center rounded-full text-lg text-muted-foreground hover:bg-muted hover:text-foreground">‹</button>
                <button type="button" onClick={() => { setSelected(null); setView({ y: now.getUTCFullYear(), m: now.getUTCMonth() }); }} className="min-h-11 rounded-full px-2 font-display text-sm font-bold">
                  {monthLabel}
                </button>
                <button type="button" onClick={() => move(1)} aria-label={t("retailCal", "nextMonth")} className="inline-flex h-11 w-11 items-center justify-center rounded-full text-lg text-muted-foreground hover:bg-muted hover:text-foreground">›</button>
              </div>
              <div className="mt-1 grid grid-cols-7 text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {weekdays.map((w, i) => <span key={i} className="py-1">{w}</span>)}
              </div>
              <div className="grid grid-cols-7 gap-y-0.5">
                {cells.map((d, i) => {
                  if (!d) return <span key={`e${i}`} />;
                  // Un día: punto. Campañas de varios días: línea continua apilada por carriles.
                  const dots = marked.filter((e) => !isSpan(e) && e.start === d);
                  const spans = laneOf.lanes.map((lane) => lane.find((e) => covers(e, d)) ?? null);
                  const evs = [...dots, ...(spans.filter(Boolean) as CalendarItem[])];
                  const col = i % 7;
                  const isSel = selected === d;
                  const isToday = d === todayIso;
                  return (
                    <div key={d} className="flex flex-col">
                      <button
                        type="button"
                        onClick={() => { setSelected(isSel ? null : d); if (!isSel && window.matchMedia("(max-width: 639px)").matches) setPane("dates"); }}
                        aria-pressed={isSel}
                        aria-label={`${Number(d.slice(8))} ${monthLabel}${evs.length ? ` · ${evs.map((e) => itemTitle(e, t)).join(", ")}` : ""}`}
                        className={`relative mx-auto flex h-11 w-full max-w-11 flex-col items-center justify-center rounded-xl text-xs tabular-nums transition-colors ${isSel ? "bg-foreground text-background" : evs.length ? "font-semibold text-foreground hover:bg-muted" : "text-foreground/70 hover:bg-muted"} ${isToday && !isSel ? "ring-1 ring-inset ring-foreground/40" : ""} ${d < todayIso && !isSel ? "opacity-50" : ""}`}
                      >
                        {Number(d.slice(8))}
                        <span className="mt-0.5 flex h-1.5 gap-0.5">
                          {dots.slice(0, 3).map((e) => <span key={e.id} className={`h-1.5 w-1.5 rounded-full ${dotClass(e)}`} />)}
                        </span>
                      </button>
                      {laneOf.lanes.length > 0 && (
                        <span className="flex flex-col gap-[2px] pt-[2px]" aria-hidden="true">
                          {spans.map((e, li) => {
                            if (!e) return <span key={li} className="h-[3px]" />;
                            const first = e.start === d || col === 0 || d === monthFrom;
                            const last = (e.end ?? e.start) === d || col === 6 || Number(d.slice(8)) === daysInMonth;
                            return <span key={li} className={`h-[3px] ${barClass(e)} ${first ? "ml-1 rounded-l-full" : ""} ${last ? "mr-1 rounded-r-full" : ""} ${d < todayIso ? "opacity-50" : ""}`} />;
                          })}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 border-t border-border/50 pt-1">
                <div className="flex flex-wrap items-center gap-x-1 text-xs" aria-label={t("retailCal", "legendTitle")}>
                  <Popover open={brandEdit !== null} onOpenChange={(v) => (v ? setBrandEdit(brandName) : closeBrandEdit())}>
                    <PopoverTrigger asChild>
                      <button type="button" className="inline-flex min-h-11 items-center gap-2 rounded-full px-2 font-medium text-foreground hover:bg-muted">
                        <span className="h-2.5 w-2.5 rounded-full bg-primary ring-1 ring-foreground/25" />{brandName || t("retailCal", "legendBrand")}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-[248px] rounded-2xl border-border/70 p-4">
                      <label className="text-xs font-semibold">{t("retailCal", "brandRename")}
                        <input autoFocus className={field} maxLength={40} value={brandEdit ?? ""} placeholder={t("retailCal", "legendBrand")}
                          onChange={(e) => setBrandEdit(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") closeBrandEdit(); }} />
                      </label>
                      <button type="button" onClick={closeBrandEdit} className="mt-3 min-h-11 w-full rounded-full bg-foreground text-sm font-semibold text-background">{t("retailCal", "save")}</button>
                    </PopoverContent>
                  </Popover>
                  {labelled.map((c) => (
                    <CalendarColorWheel
                      key={c}
                      open={legendEdit?.orig === c}
                      onOpenChange={(v) => (v ? setLegendEdit({ orig: c, color: c, name: labels[c] ?? "" }) : closeLegendEdit())}
                      value={legendEdit?.orig === c ? legendEdit.color : c}
                      onChange={(col) => setLegendEdit((e) => (e ? { ...e, color: col } : e))}
                      name={legendEdit?.orig === c ? legendEdit.name : labels[c]}
                      onNameChange={(v) => setLegendEdit((e) => (e ? { ...e, name: v } : e))}
                      onRemove={removeLegend}
                      trigger={
                        <button type="button" className="inline-flex min-h-11 items-center gap-2 rounded-full px-2 font-medium text-foreground hover:bg-muted">
                          <span className={`h-2.5 w-2.5 rounded-full ${DOT[c]}`} />{labels[c]}
                        </button>
                      }
                    />
                  ))}
                  <CalendarColorWheel
                    open={legendEdit?.orig === null}
                    onOpenChange={(v) => (v ? setLegendEdit({ orig: null, color: CAL_COLORS.find((c) => !labels[c]) ?? "graphite", name: "" }) : closeLegendEdit())}
                    value={legendEdit?.orig === null ? legendEdit.color : "graphite"}
                    onChange={(col) => setLegendEdit((e) => (e ? { ...e, color: col } : e))}
                    name={legendEdit?.orig === null ? legendEdit.name : ""}
                    onNameChange={(v) => setLegendEdit((e) => (e ? { ...e, name: v } : e))}
                    trigger={<button type="button" className={textBtn}>{t("retailCal", "legendAdd")}</button>}
                  />
                </div>
              </div>
            </div>

            {/* Fechas */}
            <div className={`min-w-0 flex-col px-4 py-3 ${compact ? "max-h-80 overflow-y-auto sm:flex sm:max-h-[26rem]" : "sm:flex sm:max-h-72 sm:overflow-y-auto md:max-h-[22rem]"} ${pane === "dates" ? "flex" : "hidden"}`}>
              <div className="-mt-2 mb-2 flex items-center justify-between sm:hidden">
                <button type="button" onClick={() => move(-1)} aria-label={t("retailCal", "prevMonth")} className="inline-flex h-11 w-11 items-center justify-center rounded-full text-lg text-muted-foreground hover:bg-muted">‹</button>
                <span className="font-display text-sm font-bold">{monthLabel}</span>
                <button type="button" onClick={() => move(1)} aria-label={t("retailCal", "nextMonth")} className="inline-flex h-11 w-11 items-center justify-center rounded-full text-lg text-muted-foreground hover:bg-muted">›</button>
              </div>
              {sara !== "off" && (
                <div className="-mt-1 mb-2 hidden gap-1 rounded-full bg-muted/60 p-1 sm:flex" role="tablist">
                  {(["dates", "sara"] as const).map((p) => (
                    <button key={p} type="button" role="tab" aria-selected={rightTab === p} onClick={() => setRightTab(p)}
                      className={`min-h-9 flex-1 truncate rounded-full px-2 text-xs font-semibold transition-colors ${rightTab === p ? "bg-card text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"}`}>
                      {p === "dates" ? t("retailCal", "tabDates") : tt("tabSara")}{p === "sara" && suggested.length ? ` · ${suggested.length}` : ""}
                    </button>
                  ))}
                </div>
              )}
              {showSaraRight ? saraRight : (<>
              {selected && (
                <button type="button" onClick={() => setSelected(null)} className={`${textBtn} -mt-2 self-start px-0`}>{t("retailCal", "showMonth")}</button>
              )}
              {cal.loading ? null : shown.length === 0 ? (
                <p className="text-muted-foreground">{tt(selected ? "dayEmpty" : "monthEmpty")}</p>
              ) : (
                <ul className="divide-y divide-border/60">
                  {shown.map((i) => {
                    const tag = dateTag(i.start);
                    const off = i.status === "inactive";
                    return (
                      <li key={i.id} className={`flex items-start gap-3 py-2.5 first:pt-0 ${off ? "opacity-55" : ""}`}>
                        <span className="flex w-9 shrink-0 flex-col items-center rounded-lg border border-border/70 bg-background py-1 leading-none">
                          <span className="font-display text-base font-bold tabular-nums">{tag.day}</span>
                          <span className="mt-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">{tag.mon}</span>
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-2">
                            <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dotClass(i)}`} aria-hidden="true" />
                            <span className="font-semibold leading-snug text-foreground">{itemTitle(i, t)}</span>
                            <Popover open={infoFor === i.id} onOpenChange={(v) => setInfoFor(v ? i.id : null)}>
                              <PopoverTrigger asChild>
                                <button
                                  type="button"
                                  onMouseEnter={() => hoverInfo(i.id)}
                                  onMouseLeave={() => hoverInfo(null)}
                                  aria-label={t("retailCal", "moreInfo")}
                                  className="-my-3 inline-flex h-11 w-11 shrink-0 items-center justify-center"
                                >
                                  <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full border font-display text-[11px] font-bold italic leading-none ${infoFor === i.id ? "border-foreground bg-foreground text-background" : "border-foreground/40 text-foreground/70"}`}>i</span>
                                </button>
                              </PopoverTrigger>
                              <PopoverContent align="start" sideOffset={4} onOpenAutoFocus={(e) => e.preventDefault()}
                                onMouseEnter={() => hoverInfo(i.id)} onMouseLeave={() => hoverInfo(null)}
                                className="w-[280px] rounded-2xl border-border/70 p-4 shadow-lg">
                            <div>
                              {(i.source === "ai_suggestion" || i.source === "user_custom") && (
                                <span className="block text-[11px] uppercase tracking-wider text-muted-foreground">{(i.source === "ai_suggestion" ? tt("src_ai") : t("retailCal", "src_user"))}</span>
                              )}
                              {i.category === "legal" && !i.systemKey?.startsWith("grant:") && (
                                <span className="mt-1 block text-xs leading-snug text-foreground/80">{t("retailCal", cal.kind === "hospitality" ? "legalHosp" : "legalRetail")} {t("retailCal", "legalNote")}</span>
                              )}
                              {i.notes && noteFor !== i.id && <span className="mt-1 block text-xs italic text-foreground/70">{i.notes}</span>}
                              {noteFor === i.id ? (
                                <input
                                  autoFocus
                                  defaultValue={i.notes ?? ""}
                                  maxLength={400}
                                  placeholder={t("retailCal", "notesPh")}
                                  aria-label={t("retailCal", "notes")}
                                  onBlur={(e) => saveNote(i, e.target.value)}
                                  onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
                                  className={field}
                                />
                              ) : (
                                <span className="-ml-2 mt-1 flex flex-wrap">
                                  <button type="button" onClick={() => setNoteFor(i.id)} className={textBtn}>{t("retailCal", "note")}</button>
                                  {i.source === "user_custom" && (
                                    <>
                                      <button type="button" onClick={() => setForm({ id: i.rowId!, start: i.start, end: i.end ?? "", title: i.title ?? "", reason: i.reason ?? "", lead: i.lead, color: i.color ?? "graphite" })} className={textBtn}>{t("retailCal", "edit")}</button>
                                      <button type="button" onClick={() => remove(i)} className={textBtn}>{t("retailCal", "delete")}</button>
                                    </>
                                  )}
                                </span>
                              )}
                            </div>
                              </PopoverContent>
                            </Popover>
                          </span>
                          {itemReason(i, t) && <span className="mt-0.5 line-clamp-2 block text-xs leading-snug text-foreground/70">{itemReason(i, t)}</span>}
                          {i.notes && <span className="mt-1 block text-xs italic text-foreground/70">{i.notes}</span>}
                        </span>
                        <span className="flex min-h-11 shrink-0 items-center">
                          <Switch checked={i.status === "active"} onCheckedChange={(v) => setStatus(i, v ? "active" : "inactive")} aria-label={`${t("retailCal", "active")}: ${itemTitle(i, t)}`} />
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
              </>)}
            </div>
          </div>

          {/* Conversación con el asistente: franja inferior; sus propuestas se ven en la columna derecha */}
          {sara !== "off" && !form && (
            <section className="mx-4 mb-3 rounded-2xl border border-foreground/10 bg-gradient-to-b from-primary/15 to-card px-4 py-3" aria-live="polite">
              <div className="flex items-center justify-between gap-3">
                <p className="font-display text-sm font-bold">{aiName}</p>
                <span className="-my-2 flex">
                  <button type="button" onClick={openBigChat} className={textBtn}>{t("retailCal", "saraExpand")}</button>
                  <button type="button" onClick={() => { setSara("off"); setRightTab("dates"); }} className={textBtn}>{tt("saraClose")}</button>
                </span>
              </div>
              <p className="text-[13px] leading-snug text-foreground/85">
                {!place ? tt("radarIntroNoPlace")
                  : sara === "loading" ? tt("saraWorking")
                  : sara === "error" ? tt("radarError")
                  : suggested.length ? t("retailCal", "saraFound")
                  : t("retailCal", "saraMore").replace("{month}", monthLabel)}
              </p>
              {place && (
                <div className="mt-2">
                  <div className="-mx-1 flex flex-wrap gap-1">
                    {(["saraAskLocal", "saraAskFairs", "saraAskSuppliers"] as const).map((k) => (
                      <button key={k} type="button" disabled={sara === "loading"} onClick={() => askSara(monthKey, t("retailCal", k))} className="min-h-11 rounded-full border border-border/80 bg-card px-3 text-xs font-medium hover:bg-muted disabled:opacity-50">
                        {t("retailCal", k)}
                      </button>
                    ))}
                  </div>
                  <form className="mt-2 flex gap-2" onSubmit={(e) => { e.preventDefault(); const q = ask.trim(); if (q && sara !== "loading") { setAsk(""); askSara(monthKey, q); } }}>
                    <input value={ask} onChange={(e) => setAsk(e.target.value)} maxLength={300} placeholder={tt("saraAskPh")} aria-label={tt("saraAskPh")} className={`${field} mt-0 flex-1`} />
                    <button type="submit" disabled={!ask.trim() || sara === "loading"} className="min-h-11 shrink-0 rounded-full bg-foreground px-4 text-xs font-semibold text-background disabled:opacity-40">{t("retailCal", "saraAskSend")}</button>
                  </form>
                </div>
              )}
            </section>
          )}

          {/* Fecha propia */}
          {form && (
            <section className="mx-4 my-3 rounded-2xl border border-border/80 bg-background p-4">
              <div className="-ml-2 -mt-2 mb-2 flex items-center gap-1">
                <button type="button" onClick={() => setForm(null)} className="inline-flex min-h-11 items-center px-2 text-sm font-semibold text-muted-foreground hover:text-foreground">‹ {t("retailCal", "backToList")}</button>
                <span className="ml-auto font-display text-sm font-bold">{t("retailCal", form.id ? "editDate" : "newDate")}</span>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="text-xs font-semibold">{t("retailCal", "fDate")}<input type="date" className={field} value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} /></label>
                <label className="text-xs font-semibold">{t("retailCal", "fEnd")}<input type="date" className={field} value={form.end} min={form.start} onChange={(e) => setForm({ ...form, end: e.target.value })} /></label>
                <label className="text-xs font-semibold sm:col-span-2">{t("retailCal", "fTitle")}<input className={field} maxLength={120} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></label>
                <label className="text-xs font-semibold sm:col-span-2">{t("retailCal", "fReason")}<textarea className={`${field} min-h-20 py-2`} maxLength={600} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></label>
                <label className="text-xs font-semibold">{t("retailCal", "fLead")}
                  <select className={field} value={form.lead} onChange={(e) => setForm({ ...form, lead: Number(e.target.value) })}>
                    {LEADS.map((n) => <option key={n} value={n}>{t("retailCal", "leadDays").replace("{n}", String(n))}</option>)}
                  </select>
                </label>
                <fieldset className="text-xs font-semibold">
                  <legend>{t("retailCal", "fColor")}</legend>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {labelled.map((c) => (
                      <button key={c} type="button" onClick={() => setForm({ ...form, color: c })} aria-pressed={form.color === c} className={`inline-flex min-h-11 items-center gap-2 rounded-full border px-3 text-xs font-medium ${form.color === c ? "border-foreground" : "border-border/70 hover:bg-muted"}`}>
                        <span className={`h-3 w-3 rounded-full ${DOT[c]}`} />{labels[c]}
                      </button>
                    ))}
                    <CalendarColorWheel
                      open={formWheel}
                      onOpenChange={setFormWheel}
                      value={form.color}
                      onChange={(c) => setForm({ ...form, color: c })}
                      trigger={
                        <button type="button" aria-label={t("retailCal", "pickTone")} className={`inline-flex min-h-11 items-center gap-2 rounded-full border px-3 text-xs font-medium ${labels[form.color] ? "border-border/70" : "border-foreground"} hover:bg-muted`}>
                          <span className={`h-3 w-3 rounded-full ${DOT[form.color]}`} />{labels[form.color] ? t("retailCal", "pickTone") : colorName(form.color)}
                        </button>
                      }
                    />
                  </div>
                </fieldset>
              </div>
              <div className="mt-4 flex gap-2">
                <button type="button" onClick={submitForm} disabled={!form.start || !form.title.trim()} className="min-h-11 rounded-full bg-foreground px-5 text-sm font-semibold text-background disabled:opacity-40">{t("retailCal", "save")}</button>
                <button type="button" onClick={() => setForm(null)} className="min-h-11 rounded-full px-4 text-sm text-muted-foreground hover:bg-muted">{t("retailCal", "cancel")}</button>
              </div>
            </section>
          )}

          <div className={`grid-cols-1 gap-2 border-t border-border/70 px-4 py-3 sm:grid-cols-2 ${form ? "hidden" : "grid"}`}>
            {sara === "off" ? (
              <button type="button" onClick={() => askSara()} className={`min-h-11 w-full truncate rounded-full border border-foreground bg-primary px-3 text-sm font-semibold text-foreground shadow-xs hover:brightness-95 ${pane === "month" ? "block" : "hidden"} sm:block`}>
                {tt(marked.length ? "saraCtaReview" : "saraCta")}
              </button>
            ) : <span className="hidden sm:block" />}
            {!form ? (
              <button type="button" onClick={() => setForm({ ...emptyForm, start: selected ?? (monthFrom > todayIso ? monthFrom : todayIso) })} className={`min-h-11 w-full truncate rounded-full border border-border bg-card px-3 text-sm font-semibold hover:bg-muted ${pane === "dates" ? "block" : "hidden"} sm:block`}>
                {t("retailCal", "addOwn")}
              </button>
            ) : <span className="hidden sm:block" />}
          </div>
        </div>
      )}
    </section>
  );
}
